import { HttpClient } from '@angular/common/http';
import {
  EventEmitter,
  Injectable,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Task } from '@models';
import {
  MessageService,
  MlTrackingService,
  SimulationService,
  DrawService,
} from '@services';
import { map, take } from 'rxjs';
import { SceneStore } from '../stores/scene.store';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private drawService = inject(DrawService);
  private http = inject(HttpClient);
  private messageService = inject(MessageService);
  private mlTrackingService = inject(MlTrackingService);
  private sceneStore = inject(SceneStore);
  private simulationService = inject(SimulationService);

  constructor() {
    effect(() => {
      if (this.sceneStore.data().identifier) {
        this.http
          .get<Task[]>(this.sceneStore.path() + '/tasks.json')
          .pipe(
            take(1),
            map((tasks) => {
              let measureMetrics = false;
              return tasks.map((task, index) => {
                if (tasks[index - 1]?.start) {
                  measureMetrics = true;
                } else if (tasks[index - 1]?.end) {
                  measureMetrics = false;
                }

                return { ...task, measureMetrics };
              });
            }),
          )
          .subscribe((tasks) => {
            this.setTaskList(tasks);
            this.restartTasks();
          });
      }
    });
  }

  skippedTask = new EventEmitter<void>();
  nextTask = new EventEmitter<void>();

  taskList: Task[] = [];
  currentTaskIndex = 0;
  length = 0;

  currentTask = signal<Task>(this.taskList[0]);
  currentTaskId = computed(() => this.currentTask()?.identifier);

  setTaskList(tasks: Task[]) {
    this.resetTasks();
    this.taskList = tasks.map((task) => ({ ...task, time: 0 }));
    this.currentTask.set(this.taskList[0]);
    this.length = tasks.length;
    if (!this.taskList.find((task) => task.start))
      this.taskList[0] = {
        ...this.taskList[0],
        start: true,
      };
    if (!this.taskList.find((task) => task.end))
      this.taskList[this.length] = {
        ...this.taskList[this.length - 1],
        end: true,
      };
  }

  skipTask() {
    const skip = this.taskList[this.currentTaskIndex]['state'] !== 'upComing';
    this.mlTrackingService.track({ event: 'skip' });
    this.skippedTask.emit();
    this.nextTask.emit();
    if (this.currentTaskIndex < this.taskList.length) {
      this.taskList[this.currentTaskIndex]['state'] = skip
        ? 'skipped'
        : this.taskList[this.currentTaskIndex]['state'];
      this.taskList[this.currentTaskIndex]['time'] =
        this.simulationService.recordingTime() -
        this.taskList.reduce((acc, task) => acc + (task.time ?? 0), 0);
      if (this.currentTaskIndex !== this.taskList.length - 1) {
        this.taskList[this.currentTaskIndex + 1]['state'] = 'inProgress';
        this.currentTaskIndex = this.currentTaskIndex + 1;
        this.currentTask.set(this.taskList[this.currentTaskIndex]);
      }
    }
    this.sceneStore.updateTaskstate(
      this.taskListCompleted(),
      this.allTasksSkipped(),
    );
    this.timerHandler();
    this.drawService.clearCanvas('static');
    this.messageService.killDebugMessage();
  }

  async completeTask(identifier: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        if (
          this.taskList &&
          this.taskList.find(
            (task) =>
              task.identifier === identifier &&
              (task.state === 'inProgress' || task.state === 'upComing'),
          )
        ) {
          this.taskList = this.taskList.map((task) =>
            task.identifier === identifier
              ? {
                  ...task,
                  state: 'completed',
                  time:
                    this.simulationService.recordingTime() -
                    this.taskList.reduce(
                      (acc, task) => acc + (task.time ?? 0),
                      0,
                    ),
                }
              : task.state === 'inProgress'
                ? { ...task, state: 'upComing' }
                : task,
          );

          if (this.currentTaskIndex < this.length - 1) {
            this.currentTaskIndex = this.currentTaskIndex + 1;
            this.taskList[this.currentTaskIndex] = {
              ...this.taskList[this.currentTaskIndex],
              state: 'inProgress',
            };
            this.currentTask.set(this.taskList[this.currentTaskIndex]);
          }
        }
        this.sceneStore.updateTaskstate(
          this.taskListCompleted(),
          this.allTasksSkipped(),
        );
        this.timerHandler();
        this.nextTask.emit();
        this.messageService.killDebugMessage();
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  timerHandler() {
    if (this.taskListCompleted()) {
      this.simulationService.stopRecordingTimer();
    }
    if (this.taskList[this.currentTaskIndex - 1].start) {
      this.simulationService.startRecordingTimer();
    }
    if (this.taskList[this.currentTaskIndex - 1].end) {
      this.simulationService.stopRecordingTimer();
    }
  }

  measureMetrics(): boolean {
    return this.currentTask().measureMetrics;
  }

  taskListCompleted(): boolean {
    return (
      (this.taskList.length > 0 &&
        this.taskList[this.taskList.length - 1]?.state === 'completed') ||
      this.taskList[this.taskList.length - 1]?.state === 'skipped'
    );
  }

  allTasksSkipped(): boolean {
    return this.taskList.every((task) => task.state === 'skipped');
  }

  restartTasks(): void {
    this.taskList = this.taskList.map((task) => ({
      ...task,
      state: 'upComing',
    }));
    this.taskList[0] = { ...this.taskList[0], state: 'inProgress' };
    this.currentTaskIndex = 0;
    this.currentTask.set(this.taskList[0]);
  }

  resetTasks() {
    this.taskList = [];
  }
}
